import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Select, Input, Field, ButtonGroup } from './UIComponents';
import { Loader } from '@hexure/ui';
import { STATES } from '../../constants/App';
import HealthAnalyzerApi from '../../utils/HealthAnalyzerApi.js';
import CarriersModal from './CarriersModal';
import { HealthAnalyzer } from '../../constants/HealthAnalyzer.js';
import { mdiClipboardTextOutline } from '@mdi/js';
import Icon from '@mdi/react';

const healthConditions = HealthAnalyzer.HEALTH_CONDITIONS;

const aviationActivities = HealthAnalyzer.AVIATION_ACTIVITIES;

const hazardousAvocations = HealthAnalyzer.HAZARDOUS_AVOCATIONS;

const cancerTypes = HealthAnalyzer.CANCER_TYPES;

/** --- UTILITIES --- */
const styles = {
  overlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '85vh',
    maxWidth: 600,
    overflowY: 'auto',
    padding: 24,
    width: '90%'
  },
  header: {
    alignItems: 'center',
    borderBottom: '1px solid #e7e6e6',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 12
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: '#333'
  },
  content: {
    flex: 1
  },
  footer: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    marginTop: 20
  },
  carriersList: {
    borderTop: '1px solid #e7e6e6',
    maxHeight: 200,
    overflowY: 'auto',
    marginTop: 16,
    paddingRight: 8
  },
  carrierItem: selected => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    borderRadius: 4,
    cursor: 'pointer',
    backgroundColor: selected ? '#e3f2fd' : 'transparent',
    borderLeft: selected ? '3px solid #1976d2' : '3px solid transparent'
  }),
  chip: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    borderRadius: 20,
    padding: '6px 10px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginRight: 8,
    marginBottom: 8
  }
};

const getExactAge = input => {
  if (!input && input !== 0) {
    return null;
  }

  let birth = null;

  // If it's already a Date
  if (input instanceof Date) {
    birth = input;
  } else if (typeof input === 'number') {
    // timestamp
    birth = new Date(input);
  } else if (typeof input === 'string') {
    const s = input.trim();
    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [Y, M, D] = s.split('-').map(Number);
      birth = new Date(Y, M - 1, D);
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
      // MM/DD/YYYY
      const [m, d, y] = s.split('/').map(Number);
      birth = new Date(y, m - 1, d);
    } else {
      // fallback to Date.parse for other formats
      const parsed = Date.parse(s);
      if (!isNaN(parsed)) birth = new Date(parsed);
    }
  }

  if (!birth || isNaN(birth.getTime())) {
    return null;
  }

  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : null;
};

// Carrier id mapping is handled upstream (e.g. in UniversalQuoterComponent)
// based on the live carrier list data. This modal only manages the selected ids.

/** --- SMALL REUSABLE PARTS --- */
const LabeledSelect = ({ label, value, onChange, options, required, placeholder, style, disabled }) => (
  <Field label={label} required={required} style={style}>
    <Select value={value} onChange={onChange} options={options} placeholder={placeholder} disabled={disabled} />
  </Field>
);

const LabeledInput = ({ label, ...rest }) => (
  <Field label={label} required={rest?.required}>
    <Input {...rest} />
  </Field>
);

const ToggleGroup = ({ label, yesActive, onYes, onNo, required }) => (
  <Field label={label} required={required}>
    <ButtonGroup>
      <Button type='button' variant={yesActive ? 'active' : 'default'} onClick={onYes}>
        Yes
      </Button>
      <Button type='button' variant={!yesActive ? 'active' : 'default'} onClick={onNo}>
        No
      </Button>
    </ButtonGroup>
  </Field>
);

const CheckboxGrid = ({ options, valueArray, onToggle }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px,1fr))', gap: 8, marginTop: 8 }}>
    {options.map(opt => {
      const checked = valueArray.includes(opt.value);
      return (
        <label key={opt.value} style={{ display: 'flex', alignItems: 'center', fontSize: 14 }}>
          <input type='checkbox' checked={checked} onChange={() => onToggle(opt.value, !checked)} style={{ marginRight: 6 }} />
          {opt.label}
        </label>
      );
    })}
  </div>
);

/** --- MAIN COMPONENT --- */
const HealthAnalyzerModal = ({
  onClose,
  areCarriersEqual,
  availableCarriers,
  dateOfBirth,
  faceAmount,
  gender,
  showReasoning = false,
  jurisdiction,
  selectedCarriers = [],
  setSelectedCarriers,
  setUnderwritingClasses,
  // optional callback so parent can know exactly which HA carriers were evaluated
  onHaCarriersEvaluated,
  isolated = false
}) => {
  const [step, setStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasHistoryOfChronicIllness, setHasHistoryOfChronicIllness] = useState(false);
  const [showCarriersModal, setShowCarriersModal] = useState(false);

  const initialAge = useMemo(() => getExactAge(dateOfBirth), [dateOfBirth]);
  const initialJurisdiction = useMemo(() => STATES.find(s => s.value === jurisdiction)?.code || STATES[0].code, [jurisdiction]);

  const [haSelectedCarriers, setHaSelectedCarriers] = useState((selectedCarriers || []).map(Number).filter(v => !Number.isNaN(v)));
  const [hasInitializedCarriers, setHasInitializedCarriers] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Quote data
    age: initialAge,
    faceAmount,
    gender: gender === 'Male' ? 1 : 2,
    jurisdiction: initialJurisdiction,

    // Step 2: Personal Health
    takesBloodPressureMedication: false,
    cholesterolMedication: 3,
    diastolicReading: '80',
    hdlReading: 100,
    heightFeet: '5',
    heightInch: '7',
    hypertension: false,
    bloodPressureControl: 1,
    healthConditions: [],
    systolicReading: '120',
    totalCholesterol: 200,
    weight: 160,
    // new fields for describing other conditions
    describeOtherConditions: false,
    otherPersonalConditions: '',

    // Step 3: Health & Lifestyle
    avocationExperienceCertification: '',
    engagesInNonCommercialAviation: false,
    selectedAviationActivities: [],
    hasDrugOrAlcoholAbuse: false,
    engagesInHazardousAvocation: false,
    selectedHazardousAvocations: [],
    tobaccoUse: 1,
    tobaccoFrequency: null,
    yearsSinceAbuseCeased: 0,

    // Step 5: Driving History
    hasAccidents: false,
    accidentCount: 0,
    hasDUI: false,
    dUICount: 0,
    hasSuspension: false,
    suspensionCount: 0,
    hasMovingViolations: false,
    movingViolationCount: 0,
    hasRecklessDriving: false,
    recklessDrivingCount: 0,

    showReasoning: showReasoning
  });

  const [familyHistory, setFamilyHistory] = useState([{ ageAtDeathOrDiagnosis: 0, relationship: '', isDeath: false, type: '', cancerType: null }]);
  const [familyHistoryYN, setFamilyHistoryYN] = useState('no');

  // Derived validation for family history step
  const familyHistoryInvalid = useMemo(() => {
    if (familyHistoryYN !== 'yes') return false;
    if (!Array.isArray(familyHistory) || familyHistory.length === 0) return false;
    return familyHistory.some(entry => !entry.relationship || !entry.type || (entry.type === '4' && !entry.cancerType));
  }, [familyHistoryYN, familyHistory]);

  /** --- DERIVED --- */
  // Normalize availableCarriers to support multiple shapes ({ value,label } or { id,name } etc.)
  const normalizedAvailableCarriers = useMemo(() => {
    if (!Array.isArray(availableCarriers)) return [];
    return availableCarriers.map(carrier => {
      if (!carrier) return { value: '', label: '' };
      const value = carrier.value ?? carrier.id ?? carrier.carrier_id ?? carrier.carrier ?? '';
      const label = carrier.label ?? carrier.name ?? carrier.carrier_name ?? carrier.display_name ?? String(value);
      return { value, label, raw: carrier };
    });
  }, [availableCarriers]);

  // When the modal first opens and no HA carriers are selected yet, default to all
  useEffect(() => {
    if (!hasInitializedCarriers && Array.isArray(availableCarriers) && availableCarriers.length) {
      const allIds = availableCarriers
        .map(c => c.value ?? c.id ?? c.carrier_id ?? c.carrier)
        .map(Number)
        .filter(v => !Number.isNaN(v));
      if (allIds.length) {
        setHaSelectedCarriers(allIds);
        if (!isolated && setSelectedCarriers && typeof setSelectedCarriers === 'function') {
          setSelectedCarriers(allIds);
        }
        setHasInitializedCarriers(true);
      }
    }
  }, [availableCarriers, hasInitializedCarriers, isolated, setSelectedCarriers]);

  const hasCarrierSelected = haSelectedCarriers.length > 0;
  // Validate height and weight
  const invalidHeight = useMemo(() => {
    const feet = formData.heightFeet;
    const inch = formData.heightInch;
    const allowedFeet = new Set(Array.from({ length: 8 }, (_, i) => String(i + 1))); // '1'..'8'
    const allowedInches = new Set(Array.from({ length: 12 }, (_, i) => String(i))); // '0'..'11'
    return !(allowedFeet.has(String(feet)) && allowedInches.has(String(inch)));
  }, [formData.heightFeet, formData.heightInch]);

  const invalidWeight = useMemo(() => {
    const w = Number(formData.weight);
    return Number.isNaN(w) || w <= 0;
  }, [formData.weight]);

  const hasWeightAndHeight = !invalidHeight && !invalidWeight;
  const hasValidReadings = useMemo(() => {
    const systolic = Number(formData.systolicReading);
    const diastolic = Number(formData.diastolicReading);
    const totalChol = Number(formData.totalCholesterol);
    const hdl = Number(formData.hdlReading);

    return !Number.isNaN(systolic) && systolic > 0 && !Number.isNaN(diastolic) && diastolic > 0 && !Number.isNaN(totalChol) && totalChol > 0 && !Number.isNaN(hdl) && hdl > 0;
  }, [formData.systolicReading, formData.diastolicReading, formData.totalCholesterol, formData.hdlReading]);

  // Individual invalid flags for visual state / accessibility
  const invalidSystolic = useMemo(() => {
    const v = Number(formData.systolicReading);
    return Number.isNaN(v) || v <= 0;
  }, [formData.systolicReading]);

  const invalidDiastolic = useMemo(() => {
    const v = Number(formData.diastolicReading);
    return Number.isNaN(v) || v <= 0;
  }, [formData.diastolicReading]);

  const invalidTotalChol = useMemo(() => {
    const v = Number(formData.totalCholesterol);
    return Number.isNaN(v) || v <= 0;
  }, [formData.totalCholesterol]);

  const invalidHdl = useMemo(() => {
    const v = Number(formData.hdlReading);
    return Number.isNaN(v) || v <= 0;
  }, [formData.hdlReading]);

  /** --- STATE HELPERS --- */
  const updateField = useCallback((key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleInArray = useCallback((key, value, nextChecked) => {
    setFormData(prev => {
      const arr = prev[key] || [];
      const next = nextChecked ? [...arr, value] : arr.filter(v => v !== value);
      return { ...prev, [key]: next };
    });
  }, []);

  const setBothCarriers = useCallback(
    next => {
      setHaSelectedCarriers(prev => {
        let nextHa;
        if (typeof next === 'function') nextHa = next(prev);
        else if (Array.isArray(next)) nextHa = next;
        else {
          const id = Number(next);
          nextHa = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
        }
        const validSet = new Set(normalizedAvailableCarriers.map(c => Number(c.value)));
        nextHa = Array.from(new Set(nextHa.map(Number))).filter(id => validSet.has(id));
        setSelectedCarriers(nextHa);
        return nextHa;
      });
    },
    [normalizedAvailableCarriers, setSelectedCarriers]
  );

  // Transform availableCarriers to the format expected by CarriersModal (id/name)
  const carriersModalData = useMemo(
    () => normalizedAvailableCarriers.map(carrier => ({ id: carrier.value, name: carrier.label, carrier_id: carrier.value, carrier_name: carrier.label, display_name: carrier.label })),
    [normalizedAvailableCarriers]
  );

  // Handle carriers modal changes
  const handleCarriersChange = useCallback(
    newCarriers => {
      const carrierIds = newCarriers.map(c => (typeof c === 'object' ? c.id : c));
      setHaSelectedCarriers(carrierIds);
      // Only update parent if not isolated
      if (!isolated && setSelectedCarriers && typeof setSelectedCarriers === 'function') {
        setSelectedCarriers(carrierIds);
      }
    },
    [setSelectedCarriers, isolated]
  );

  // Transform haSelectedCarriers to format expected by CarriersModal
  const selectedCarriersForModal = useMemo(
    () => haSelectedCarriers.map(id => ({ id: id, name: normalizedAvailableCarriers.find(c => c.value === id)?.label || 'Unknown Carrier' })),
    [haSelectedCarriers, normalizedAvailableCarriers]
  );

  useEffect(() => {
    // keep parent in sync even if the mapping changed
    // Only update parent if not isolated
    if (!isolated && setSelectedCarriers && typeof setSelectedCarriers === 'function') {
      setSelectedCarriers(haSelectedCarriers);
    }
  }, [haSelectedCarriers, isolated]);

  const mapFormDataToQuestions = useCallback(fd => {
    const out = [];
    out.push({ id: 'Height', feet: Number(fd.heightFeet), inches: Number(fd.heightInch) });

    if (fd.healthConditions?.length) out.push({ id: 'healthConditions', answers: fd.healthConditions });
    if (fd.selectedAviationActivities?.length) out.push({ id: 'selectedAviationActivities', answers: fd.selectedAviationActivities });
    if (fd.selectedHazardousAvocations?.length) out.push({ id: 'selectedHazardousAvocations', answers: fd.selectedHazardousAvocations });

    Object.entries(fd).forEach(([key, value]) => {
      if (['heightFeet', 'heightInch', 'healthConditions', 'selectedAviationActivities', 'selectedHazardousAvocations'].includes(key)) return;
      if (key === 'tobaccoFrequency' && fd.tobaccoUse === 1) return;
      out.push({ id: key, answer: value });
    });

    return out;
  }, []);

  const handleNext = () => {
    // Extra validation for Personal Health step (step index 1)
    if (step === 1) {
      if (invalidHeight || invalidWeight || !hasValidReadings) {
        // don't advance if personal health validation fails
        return;
      }
    }
    setStep(s => s + 1);
  };
  const handleBack = () => (step === 0 ? onClose() : setStep(s => s - 1));

  const handleSubmit = async () => {
    setStep(s => s + 1);
    const payload = {
      source: 'api',
      carriers: haSelectedCarriers,
      healthRequest: { questions: mapFormDataToQuestions(formData) }
    };

    // Only include familyHistory when user answered 'yes'
    if (familyHistoryYN === 'yes') {
      payload.familyHistory = familyHistory;
    }
    try {
      const res = await HealthAnalyzerApi.postHealthAnalyzerForm(payload);
      // Surface HA evaluation to parent so it can map HA carriers to ALQ carriers
      // and drive life quote carrier_ids from ONLY non-declined carriers.
      if (onHaCarriersEvaluated && typeof onHaCarriersEvaluated === 'function') {
        // Support multiple HA response shapes:
        // - raw array: [ {...}, {...} ]
        // - object with results array: { results: [ ... ] }
        // - object with data array: { data: [ ... ] }
        let resultsArray = [];

        if (Array.isArray(res)) {
          resultsArray = res;
        } else if (Array.isArray(res?.results)) {
          resultsArray = res.results;
        } else if (Array.isArray(res?.data)) {
          resultsArray = res.data;
        }

        // Treat anything with a non-declined decision as eligible. Default to
        // eligible when decision is missing so we do not accidentally drop
        // carriers if HA omits that field.
        const nonDeclined = resultsArray.filter(r => {
          const decision = String(r?.decision || '').toLowerCase();
          return decision === '' || decision === 'approved' || decision === 'standard' || decision === 'eligible' || decision === 'table' || decision === 'substandard';
        });

        const carriers = nonDeclined
          .map(r => ({
            id: r.carrier_id || r.carrier || r.carrierId || r.id,
            name: r.carrier_name || r.carrier_name_text || r.carrier || r.name
          }))
          .filter(c => c.id != null || c.name);

        const healthClasses = resultsArray.map(r => r.underwriting_class || r.health_class || r.class).filter(Boolean);
        const productIds = resultsArray.map(r => r.product_id || r.productId).filter(Boolean);

        onHaCarriersEvaluated({ carriers, healthClasses, productIds });
      }

      // Preserve existing behavior for downstream HA detail consumers.
      setUnderwritingClasses(res);
      onClose();
    } catch (err) {
      console.error('API Error:', err);
      setUnderwritingClasses([]);
    }
  };

  /** --- STEP RENDERS --- */
  const StepCarriers = (
    <>
      <p>
        Using the information you provided and the answers to the following questions we will estimate your client's health classification based on each company's current underwriting guidelines. This
        estimated underwriting class will be applied to your quote on the following screen.
      </p>
      <p>Your client's final classification and rates may vary as a result of additional information gathered by the insurance company during their underwriting process.</p>
      {!areCarriersEqual && <p>Not all carriers selected are supported for health class underwriting. Available carriers are displayed below.</p>}

      <Field label='Select your carriers to get started:' required>
        <Button onClick={() => setShowCarriersModal(true)} type='button' variant='secondary' style={{ width: '100%', marginBottom: '16px' }}>
          <Icon path={mdiClipboardTextOutline} size={0.7} /> Select Carriers ({(haSelectedCarriers || []).length} Selected)
        </Button>

        {haSelectedCarriers.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {haSelectedCarriers
              .filter(v => availableCarriers.some(c => c.value === v))
              .map(v => {
                const carrier = availableCarriers.find(c => c.value === v);
                return (
                  <span key={v} style={styles.chip}>
                    {carrier?.label}
                    <button type='button' onClick={() => setBothCarriers(haSelectedCarriers.filter(x => x !== v))} style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer' }}>
                      ×
                    </button>
                  </span>
                );
              })}
          </div>
        )}
      </Field>
    </>
  );

  const StepPersonalHealth = (
    <>
      <div style={{ display: 'flex', gap: 16 }}>
        <Field label='Height' required style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <Select
              value={formData.heightFeet}
              onChange={e => {
                const raw = e.target.value;
                const parsed = Number(raw);
                updateField('heightFeet', Number.isNaN(parsed) ? '' : String(parsed));
              }}
              options={Array.from({ length: 9 }, (_, i) => ({ value: String(i), label: String(i) }))}
              placeholder='ft'
              style={{ width: 80, border: invalidHeight ? '1px solid #d32f2f' : undefined, borderRadius: 4 }}
              aria-invalid={invalidHeight}
            />
            <Select
              value={formData.heightInch}
              onChange={e => {
                const raw = e.target.value;
                const parsed = Number(raw);
                updateField('heightInch', Number.isNaN(parsed) ? '' : String(parsed));
              }}
              options={Array.from({ length: 12 }, (_, i) => ({ value: String(i), label: String(i) }))}
              placeholder='in'
              style={{ width: 80, border: invalidHeight ? '1px solid #d32f2f' : undefined, borderRadius: 4 }}
              aria-invalid={invalidHeight}
            />
          </div>
        </Field>

        <LabeledInput
          label='Weight (lbs)'
          type='number'
          min='0'
          max='1000'
          placeholder='Weight'
          value={String(formData.weight)}
          onChange={e => updateField('weight', Number(e.target.value))}
          style={{ flex: 1, border: invalidWeight ? '1px solid #d32f2f' : undefined }}
          aria-invalid={invalidWeight}
          required
        />
      </div>

      <ToggleGroup
        label='Has the proposed insured ever been told they have high blood pressure?'
        yesActive={formData.hypertension}
        onYes={() => updateField('hypertension', true)}
        onNo={() => updateField('hypertension', false)}
      />

      {formData.hypertension && (
        <>
          <LabeledSelect
            label='If yes, do you take blood pressure medication?'
            value={String(formData.takesBloodPressureMedication)}
            onChange={e => updateField('takesBloodPressureMedication', e.target.value === 'true')}
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' }
            ]}
            placeholder='Select'
          />
          <LabeledSelect
            label='How well is your blood pressure controlled?'
            value={String(formData.bloodPressureControl)}
            onChange={e => updateField('bloodPressureControl', Number(e.target.value))}
            options={[
              { value: '1', label: 'Very Well Controlled' },
              { value: '2', label: 'Reasonably Well Controlled' },
              { value: '3', label: 'Not Well Controlled' }
            ]}
            placeholder='Select'
          />
        </>
      )}

      <Field label='Last readings' required>
        <div style={{ display: 'flex', gap: 16 }}>
          <Input
            placeholder='Systolic'
            value={formData.systolicReading}
            onChange={e => updateField('systolicReading', e.target.value)}
            style={{ border: invalidSystolic ? '1px solid #d32f2f' : undefined }}
            aria-invalid={invalidSystolic}
          />
          <Input
            placeholder='Diastolic'
            value={formData.diastolicReading}
            onChange={e => updateField('diastolicReading', e.target.value)}
            style={{ border: invalidDiastolic ? '1px solid #d32f2f' : undefined }}
            aria-invalid={invalidDiastolic}
          />
        </div>
      </Field>

      <LabeledSelect
        label='Does the proposed insured take cholesterol medication?'
        value={String(formData.cholesterolMedication)}
        onChange={e => updateField('cholesterolMedication', Number(e.target.value))}
        options={[
          { value: '1', label: 'Currently take' },
          { value: '2', label: 'Used in past' },
          { value: '3', label: 'Never prescribed or taken' }
        ]}
      />

      <LabeledInput
        label='Total Cholesterol'
        type='number'
        min='0'
        max='1000'
        value={String(formData.totalCholesterol)}
        onChange={e => updateField('totalCholesterol', Number(e.target.value))}
        style={{ border: invalidTotalChol ? '1px solid #d32f2f' : undefined }}
        aria-invalid={invalidTotalChol}
        required
      />

      <LabeledInput
        label='HDL Reading'
        type='number'
        min='0'
        max='1000'
        value={String(formData.hdlReading)}
        onChange={e => updateField('hdlReading', Number(e.target.value))}
        style={{ border: invalidHdl ? '1px solid #d32f2f' : undefined }}
        aria-invalid={invalidHdl}
        required
      />
      {/*QP-8587: Temporarily removing this question as per request*/}
      {/* <ToggleGroup
        label='Has the proposed insured had a diagnosis of cancer, diabetes, stroke, cardiovascular disease, Autoimmune disease, or other serious chronic illness?'
        yesActive={hasHistoryOfChronicIllness}
        onYes={() => setHasHistoryOfChronicIllness(true)}
        onNo={() => setHasHistoryOfChronicIllness(false)}
        required
      /> */}

      {hasHistoryOfChronicIllness && (
        <>
          <Field label='If yes, select all that apply:'>
            <CheckboxGrid options={healthConditions} valueArray={formData.healthConditions} onToggle={(val, checked) => toggleInArray('healthConditions', val, checked)} />
          </Field>

          {/* New checkbox and textarea to describe other personal conditions */}
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14 }}>
              <input
                type='checkbox'
                checked={!!formData.describeOtherConditions}
                onChange={e => {
                  const checked = e.target.checked;
                  updateField('describeOtherConditions', checked);
                  if (!checked) updateField('otherPersonalConditions', '');
                }}
                style={{ marginTop: 4 }}
              />
              <span style={{ lineHeight: '18px' }}>Please provide more detail regarding the specifics of the customer's condition. In absence of a description a high severity will be assumed.</span>
            </label>

            {formData.describeOtherConditions && (
              <LabeledInput
                label=''
                as='textarea'
                rows={4}
                maxLength={200}
                placeholder='Enter details here...'
                value={formData.otherPersonalConditions || ''}
                onChange={e => updateField('otherPersonalConditions', e.target.value.slice(0, 200))}
              />
            )}
          </div>
        </>
      )}
    </>
  );

  const StepLifestyle = (
    <>
      <LabeledSelect
        label='Have they ever used tobacco?'
        required
        value={String(formData.tobaccoUse)}
        onChange={e => updateField('tobaccoUse', Number(e.target.value))}
        options={[
          { value: '1', label: 'Never' },
          { value: '2', label: 'None in the last 60 months' },
          { value: '3', label: 'None in the last 48 months' },
          { value: '4', label: 'None in the last 36 months' },
          { value: '5', label: 'None in the last 24 months' },
          { value: '6', label: 'None in the last 12 months' },
          { value: '7', label: 'Current user' }
        ]}
      />
      {formData.tobaccoUse !== 1 && (
        <LabeledSelect
          label='How frequently?'
          required
          value={formData.tobaccoFrequency != null ? String(formData.tobaccoFrequency) : ''}
          onChange={e => updateField('tobaccoFrequency', Number(e.target.value))}
          options={[
            { value: '1', label: 'Cigarettes/e-Cigarettes' },
            { value: '2', label: 'Cigar, 12 or fewer per year' },
            { value: '3', label: 'Cigar, 13-24 per year' },
            { value: '4', label: 'Cigar, 25-52 per year' },
            { value: '5', label: 'Cigar, more than 52 per year' },
            { value: '6', label: 'Pipe, 52 or fewer times per year' },
            { value: '7', label: 'Pipe, more than 52 times per year' },
            { value: '8', label: 'Nicotine patch, gum, chew, snuff' }
          ]}
        />
      )}

      <ToggleGroup
        label='Does the proposed insured have a history of drug or alcohol abuse?'
        yesActive={formData.hasDrugOrAlcoholAbuse}
        onYes={() => updateField('hasDrugOrAlcoholAbuse', true)}
        onNo={() => updateField('hasDrugOrAlcoholAbuse', false)}
        required
      />
      {formData.hasDrugOrAlcoholAbuse && (
        <LabeledInput
          label='If yes, how many years since the abuse ceased?'
          type='number'
          min='0'
          max='99'
          value={String(formData.yearsSinceAbuseCeased)}
          onChange={e => updateField('yearsSinceAbuseCeased', Number(e.target.value))}
          required
        />
      )}

      <ToggleGroup
        label='Does the proposed insured engage in non-commercial aviation?'
        yesActive={formData.engagesInNonCommercialAviation}
        onYes={() => updateField('engagesInNonCommercialAviation', true)}
        onNo={() => updateField('engagesInNonCommercialAviation', false)}
        required
      />
      {formData.engagesInNonCommercialAviation && (
        <Field label='Select all aviation activities that apply:'>
          <CheckboxGrid options={aviationActivities} valueArray={formData.selectedAviationActivities} onToggle={(val, checked) => toggleInArray('selectedAviationActivities', val, checked)} />
        </Field>
      )}

      <ToggleGroup
        label='Does the proposed insured engage in any hazardous avocation?'
        yesActive={formData.engagesInHazardousAvocation}
        onYes={() => updateField('engagesInHazardousAvocation', true)}
        onNo={() => updateField('engagesInHazardousAvocation', false)}
        required
      />
      {formData.engagesInHazardousAvocation && (
        <>
          <Field label='Select all hazardous avocations that apply:'>
            <CheckboxGrid options={hazardousAvocations} valueArray={formData.selectedHazardousAvocations} onToggle={(val, checked) => toggleInArray('selectedHazardousAvocations', val, checked)} />
          </Field>
          <LabeledInput
            label='Additional avocation information (maximum 100 characters)'
            as='textarea'
            rows={2}
            maxLength={100}
            placeholder='Enter additional info...'
            value={formData.avocationExperienceCertification || ''}
            onChange={e => updateField('avocationExperienceCertification', e.target.value.replace(/\b\d{3}-?\d{2}-?\d{4}\b/g, 'XXX-XX-XXXX'))}
          />
        </>
      )}
    </>
  );

  const StepFamilyHistory = (
    <>
      <ToggleGroup
        label='Does the proposed insured have a family history of cancer, diabetes, stroke, cardiovascular disease, Autoimmune disease, or other serious chronic illness?'
        yesActive={familyHistoryYN === 'yes'}
        onYes={() => setFamilyHistoryYN('yes')}
        onNo={() => setFamilyHistoryYN('no')}
        required
      />
      {familyHistoryYN === 'yes' && (
        <>
          {familyHistory.map((entry, idx) => {
            const relationshipOptions = [
              ...(!familyHistory.some(e => e.relationship === 2) || entry.relationship === 2 ? [{ value: 2, label: 'Father' }] : []),
              ...(!familyHistory.some(e => e.relationship === 1) || entry.relationship === 1 ? [{ value: 1, label: 'Mother' }] : []),
              { value: 3, label: 'Brother' },
              { value: 4, label: 'Sister' }
            ];
            // Validation: relationship and type required when row exists
            const relInvalid = !entry.relationship;
            const typeInvalid = !entry.type;

            return (
              <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <Field label='Relationship' required style={{ flex: 1 }}>
                  <Select
                    value={entry.relationship}
                    onChange={e => {
                      const updated = [...familyHistory];
                      updated[idx].relationship = Number(e.target.value);
                      setFamilyHistory(updated);
                    }}
                    options={relationshipOptions}
                    placeholder='Select...'
                    style={{ border: relInvalid ? '1px solid #d32f2f' : undefined }}
                    aria-invalid={relInvalid}
                  />
                </Field>

                <LabeledInput
                  label='Age'
                  type='number'
                  min='0'
                  max='120'
                  value={String(entry.ageAtDeathOrDiagnosis)}
                  onChange={e => {
                    const updated = [...familyHistory];
                    updated[idx].ageAtDeathOrDiagnosis = Number(e.target.value);
                    setFamilyHistory(updated);
                  }}
                  placeholder='Age'
                  style={{ flex: 1 }}
                />

                <Field label='Alive?' style={{ flex: 1 }}>
                  <Select
                    value={entry.isDeath === true ? 'no' : 'yes'}
                    onChange={e => {
                      const updated = [...familyHistory];
                      updated[idx].isDeath = e.target.value === 'no';
                      setFamilyHistory(updated);
                    }}
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' }
                    ]}
                  />
                </Field>

                <Field label='Type' required style={{ flex: 2 }}>
                  <Select
                    value={entry.type}
                    onChange={e => {
                      const updated = [...familyHistory];
                      updated[idx].type = e.target.value;
                      if (e.target.value !== '4') updated[idx].cancerType = '';
                      setFamilyHistory(updated);
                    }}
                    options={[
                      { value: 1, label: 'Diabetes' },
                      { value: 2, label: 'Stroke' },
                      { value: 3, label: 'Cardiovascular' },
                      { value: 4, label: 'Cancer' }
                    ]}
                    placeholder='Select...'
                    style={{ border: typeInvalid ? '1px solid #d32f2f' : undefined }}
                    aria-invalid={typeInvalid}
                  />
                </Field>
                {entry.type === '4' &&
                  (() => {
                    const cancerInvalid = !entry.cancerType;
                    return (
                      <Field label='Type of Cancer' required style={{ flex: 2 }}>
                        <Select
                          value={entry.cancerType ?? ''}
                          onChange={e => {
                            const updated = [...familyHistory];
                            updated[idx].cancerType = e.target.value;
                            setFamilyHistory(updated);
                          }}
                          options={cancerTypes}
                          disabled={entry.type !== '4'}
                          placeholder='Select...'
                          style={{ border: cancerInvalid ? '1px solid #d32f2f' : undefined }}
                          aria-invalid={cancerInvalid}
                        />
                      </Field>
                    );
                  })()}
                {/* Remove family member button (allow removing last row - resets to empty) */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    type='button'
                    variant='secondary'
                    onClick={() => {
                      const updated = [...familyHistory];
                      updated.splice(idx, 1);
                      setFamilyHistory(updated);
                      // If last row removed, reset family history selection to 'no'
                      if (updated.length === 0) {
                        setFamilyHistoryYN('no');
                      }
                    }}
                    style={{ height: 40, minWidth: 40, padding: 0 }}
                    aria-label='Remove family member'
                  >
                    ×
                  </Button>
                </div>
              </div>
            );
          })}
          <Button type='button' variant='default' onClick={() => setFamilyHistory([...familyHistory, { ageAtDeathOrDiagnosis: 0, relationship: '', type: '' }])}>
            + Add more
          </Button>
        </>
      )}
    </>
  );

  const StepDriving = (
    <>
      <ToggleGroup label='Does the client have a DWI/DUI in the last 5 years?' yesActive={formData.hasDUI} onYes={() => updateField('hasDUI', true)} onNo={() => updateField('hasDUI', false)} />
      {formData.hasDUI && (
        <LabeledInput
          label='How many DUI/DWIs?'
          type='number'
          min='0'
          max='99'
          value={String(formData.dUICount)}
          onChange={e => updateField('dUICount', Number(e.target.value))}
          placeholder='Count of DUI/DWIs'
        />
      )}

      <ToggleGroup
        label='Does the client have a license suspension in the past 5 years?'
        yesActive={formData.hasSuspension}
        onYes={() => updateField('hasSuspension', true)}
        onNo={() => updateField('hasSuspension', false)}
      />
      {formData.hasSuspension && (
        <LabeledInput
          label='How many license suspensions?'
          type='number'
          min='0'
          max='99'
          value={String(formData.suspensionCount)}
          onChange={e => updateField('suspensionCount', Number(e.target.value))}
          placeholder='Number of suspensions'
        />
      )}

      <ToggleGroup
        label='Does the customer have any motor vehicle accidents in the last 5 years?'
        yesActive={formData.hasAccidents}
        onYes={() => updateField('hasAccidents', true)}
        onNo={() => updateField('hasAccidents', false)}
      />
      {formData.hasAccidents && (
        <LabeledInput
          label='How many accidents?'
          type='number'
          min='0'
          max='99'
          value={String(formData.accidentCount)}
          onChange={e => updateField('accidentCount', Number(e.target.value))}
          placeholder='Number of accidents'
        />
      )}

      <ToggleGroup
        label='Does the client have any moving violations in the past 5 years?'
        yesActive={formData.hasMovingViolations}
        onYes={() => updateField('hasMovingViolations', true)}
        onNo={() => updateField('hasMovingViolations', false)}
      />
      {formData.hasMovingViolations && (
        <LabeledInput
          label='How many moving violations?'
          type='number'
          min='0'
          max='99'
          value={String(formData.movingViolationCount)}
          onChange={e => updateField('movingViolationCount', Number(e.target.value))}
          placeholder='Number of violations'
        />
      )}

      <ToggleGroup
        label='Reckless Driving: Did the client have a reckless driving charge in the past 5 years?'
        yesActive={formData.hasRecklessDriving}
        onYes={() => updateField('hasRecklessDriving', true)}
        onNo={() => updateField('hasRecklessDriving', false)}
      />
      {formData.hasRecklessDriving && (
        <LabeledInput
          label='How many reckless driving charges?'
          type='number'
          min='0'
          max='99'
          value={String(formData.recklessDrivingCount)}
          onChange={e => updateField('recklessDrivingCount', Number(e.target.value))}
          placeholder='Number of charges'
        />
      )}
    </>
  );

  const StepSubmitting = (
    <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      <Loader />
    </div>
  );

  const steps = [
    {
      title: 'Health Analyzer',
      render: StepCarriers,
      primary: (
        <Button onClick={handleNext} variant='primary' type='button' disabled={!hasCarrierSelected}>
          Continue
        </Button>
      )
    },
    {
      title: 'Health Analyzer - Personal Health',
      render: StepPersonalHealth,
      primary: (
        <Button onClick={handleNext} variant='primary' type='button' disabled={!hasWeightAndHeight || !hasValidReadings}>
          Continue
        </Button>
      )
    },
    {
      title: 'Health Analyzer - Health & Lifestyle',
      render: StepLifestyle,
      primary: (
        <Button onClick={handleNext} variant='primary' type='button'>
          Continue
        </Button>
      )
    },
    {
      title: 'Health Analyzer - Family History',
      render: StepFamilyHistory,
      primary: (
        <Button
          onClick={() => {
            if (familyHistoryInvalid) return;
            handleNext();
          }}
          variant='primary'
          type='button'
          disabled={familyHistoryInvalid}
        >
          Continue
        </Button>
      )
    },
    {
      title: 'Health Analyzer - Driving History',
      render: StepDriving,
      primary: (
        <Button variant='primary' type='button' onClick={handleSubmit}>
          Submit
        </Button>
      )
    },
    {
      title: 'Health Analyzer - Submit',
      render: StepSubmitting,
      primary: (
        <Button variant='primary' type='button' disabled>
          Submit
        </Button>
      )
    }
  ];

  const isFirstStep = step === 0;

  return (
    <>
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <form style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={styles.header}>
              <h2 style={styles.title}>{steps[step].title}</h2>
              <Button
                variant='secondary'
                onClick={onClose}
                style={{ fontSize: 24, width: 32, height: 32, borderRadius: 4, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ×
              </Button>
            </div>

            <div style={styles.content}>{steps[step].render}</div>

            <div style={styles.footer}>
              <Button onClick={handleBack} variant='secondary' type='button' disabled={step === steps.length - 1}>
                {isFirstStep ? 'Cancel' : 'Back'}
              </Button>
              {steps[step].primary}
            </div>
          </form>
        </div>
      </div>

      <CarriersModal
        isOpen={showCarriersModal}
        onClose={() => setShowCarriersModal(false)}
        selectedCarriers={selectedCarriersForModal.map(carrier => carrier.id)}
        onCarriersChange={handleCarriersChange}
        availableCarriers={carriersModalData}
        activeTab='health-analyzer'
      />
    </>
  );
};

HealthAnalyzerModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  dateOfBirth: PropTypes.string.isRequired,
  faceAmount: PropTypes.number.isRequired,
  gender: PropTypes.string.isRequired,
  jurisdiction: PropTypes.string.isRequired,
  areCarriersEqual: PropTypes.bool.isRequired,
  availableCarriers: PropTypes.array.isRequired,
  showReasoning: PropTypes.bool,
  selectedCarriers: PropTypes.array.isRequired,
  setSelectedCarriers: PropTypes.func.isRequired,
  setUnderwritingClasses: PropTypes.func.isRequired,
  onHaCarriersEvaluated: PropTypes.func,
  isolated: PropTypes.bool
};

export default HealthAnalyzerModal;
